<template>
  <div
    class="BaseCarousel">
    <div
      class="BaseCarousel_TrackWrapper">
      <div
        class="BaseCarousel_Track"
        :class="{ transitioning }"
        :style="computedTrackStyle"
        ref="track"
        @mousedown="handleThumbDown($event)"
        @touchstart="handleThumbDown($event)">
        <div
          class="BaseCarousel_Slide"
          :style="computedSlideStyle"
          v-for="(slide, slideIndex) in slides"
          :key="'carousel-slide-' + slideIndex"
          @click.capture.prevent.stop
          @mousedown.prevent="captureSlideClick(slide)"
          @touchstart.prevent="captureSlideClick(slide)">
          <component
            :is="slide.component"
            v-bind="slide.props"
            ref="slides">
          </component>
        </div>
      </div>

      <div
        v-if="showArrows"
        class="BaseCarousel_Arrow BaseCarousel_Arrow-left"
        @click="goToSlide(activeSlideIndex + -1, true)">
      </div>
      <div
        v-if="showArrows"
        class="BaseCarousel_Arrow BaseCarousel_Arrow-right"
        @click="goToSlide(activeSlideIndex + 1, true)">
      </div>
    </div>

    <div
      v-if="showRadios"
      key="BaseCarousel_radios"
      class="BaseCarousel_radios">
      <div
        class="BaseCarousel_radio"
        v-for="(radio, radioIndex) in radioCount"
        :class="{ 'active': radioIndex === activeSlideIndex }"
        :key="'radio-' + radioIndex"
        @click="goToSlide(radioIndex)">
      </div>
    </div>
  </div>
</template>

<script>
import { throttle } from 'lodash';

export default {
  name: 'BaseCarousel',

  data() {
    return {
      transitioning: false,
      activeSlideIndex: 0,
      dragging: false,
      dragStart: 0,
      dragEnd: 0,
      threshold: 0.25,

      slideWidth: 0,

      emitIfClick: null,
    };
  },

  props: {
    slides:     { type: Array,  required: true },
    showSlides: { type: [Number, Array], default: 1 }, // Ex: [{ size: 1200, slides: 2 }]
    showRadios: { type: Boolean, default: false },
    showArrows: { type: Boolean, default: false },
    peek:       { type: [Number, Array], default: 0 },
    gutter:     { type: Number, default: 0 },
  },

  computed: {
    numberSlides() {
      return this.slides.length;
    },

    showingSlides() {
      if (typeof this.showSlides === 'number') return this.showSlides;
      const smallerSizes = this.showSlides.slice().sort((a, b) => a.size - b.size)
        .filter((s) => s.size < this.$root.window.width);
      return smallerSizes.length ? smallerSizes.pop().slides : 1;
    },

    dragXDelta() {
      return this.dragEnd - this.dragStart;
    },

    dragDirection() {
      if (this.dragXDelta > 0) return -1;
      if (this.dragXDelta < 0) return 1;
      return 0;
    },

    trackXPosition() {
      const currentPosition = -(this.slideWidth * this.activeSlideIndex);
      return this.dragging
        ? currentPosition + this.dragXDelta
        : currentPosition;
    },

    computedTrackStyle() {
      return {
        marginLeft: `-${this.gutter}px`,
        transform: `translateX(${this.trackXPosition}px)`,
      };
    },

    computedSlideStyle() {
      return {
        minWidth: `${this.slideWidth}px`,
        padding: `0 ${this.gutter}px`,
      };
    },

    radioCount() {
      return (this.numberSlides + 1) - this.showingSlides;
    },
  },

  watch: {
    '$root.window.width': function watchWindowWidth() {
      this.setSlideWidth();
    },
  },

  mounted() {
    this.$nextTick(() => this.setSlideWidth());
  },

  activated() {
    return this.setSlideWidth();
  },

  methods: {
    captureSlideClick(slide) {
      this.emitIfClick = slide;
    },

    transition(callback = null) {
      this.transitioning = true;
      if (callback) callback();
      this.dragging = false;
      this.dragStart = 0;
      this.drageEnd = 0;
      setTimeout(() => { this.transitioning = false; }, 250);
    },

    goToSlide(slideIndex, loop = false) {
      if (slideIndex === this.activeSlideIndex) return this.transition();

      if (slideIndex < 0) {
        return this.transition(loop
          ? () => { this.activeSlideIndex = this.radioCount - 1; } : null);
      }

      if (slideIndex + this.showingSlides > this.numberSlides) {
        return this.transition(loop ? () => { this.activeSlideIndex = 0; } : null);
      }

      return this.transition(() => {
        this.activeSlideIndex = slideIndex;
      });
    },

    setSlideWidth() {
      const naturalWidth = this.$refs.track.offsetWidth / this.showingSlides;
      const peekPixels = naturalWidth * this.peek;
      const gutterPixels = this.gutter / this.showingSlides;
      this.slideWidth = naturalWidth - peekPixels + gutterPixels;
    },

    handleThumbDown(e) {
      e.preventDefault();
      if (this.transitioning) return;

      this.dragging = true;

      const eventIsTouch = e.type === 'touchstart';

      const eventType = eventIsTouch ? 'touch' : 'mouse';
      const eventEndType = eventIsTouch ? 'end' : 'up';

      this.dragStart = eventIsTouch ? e.touches[0].pageX : e.pageX;
      this.dragEnd = this.dragStart;

      this.moveListenerRef = (moveE) => this.handleThumbMove(moveE);
      this.upListenerRef = (upE) => this.handleThumbUp(eventType, eventEndType, upE);

      document.addEventListener(`${eventType}move`, this.moveListenerRef);
      document.addEventListener(`${eventType}${eventEndType}`, this.upListenerRef);
    },

    handleThumbMove: throttle(function throttleMove(e) {
      e.preventDefault();
      this.dragEnd = (e.type === 'touchmove') ? e.touches[0].pageX : e.pageX;
    }, 20),

    handleThumbUp(eventType, eventEndType, e) {
      e.preventDefault();
      document.removeEventListener(`${eventType}move`, this.moveListenerRef);
      document.removeEventListener(`${eventType}${eventEndType}`, this.upListenerRef);
      if (Math.abs(this.dragXDelta) < 10 && !!this.emitIfClick) {
        return this.$emit('click-slide', this.emitIfClick);
      }
      return this.handleSlideChange();
    },

    handleSlideChange() {
      const reachedThreshold = Math.abs(this.dragXDelta / this.slideWidth) > this.threshold;
      if (!reachedThreshold) return this.transition();
      return this.goToSlide(this.activeSlideIndex + this.dragDirection);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/_variables';

.BaseCarousel {
  position: relative;
}

.BaseCarousel_TrackWrapper {
  position: relative;
  min-width: 100%;
}

.BaseCarousel_Track {
  display: flex;
  min-width: 100%;
  flex-wrap: nowrap;
  will-change: transform;
  &.transitioning {
    transition: transform .2s ease 0s;
  }
}

.BaseCarousel_Slide {
  flex: 1 1 100%;
  > * {
    height: 100%;
  }
}

.BaseCarousel:hover .BaseCarousel_Arrow {
  opacity: .9;
}

.BaseCarousel_Arrow {
  position: absolute;
  z-index: 1;
  width: 32px;
  height: 32px;
  top: 0;
  bottom: 0;
  margin-top: auto;
  margin-bottom: auto;
  border-radius: 50%;
  background-color: $white;
  opacity: 0;
  box-shadow: 0 1px 8px rgba($black, .1);
  cursor: pointer;
  will-change: opacity;
  transition: opacity .2s ease 0s;
  &:after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    width: 12px;
    height: 12px;
    margin: auto;
    border-top: 2px solid $dark;
    border-left: 2px solid $dark;
  }
}

.BaseCarousel_Arrow-left {
  left: 8px;
  &:after { right: -4px; transform: rotate(-45deg); }
}

.BaseCarousel_Arrow-right {
  right: 8px;
  &:after { left: -4px; transform: rotate(135deg); }
}

.BaseCarousel_radios {
  position: relative;
  padding: 8px 0;
  margin: 0 auto;
  text-align: center;
}

.BaseCarousel_radio {
  display: inline-block;
  width: 8px;
  height: 8px;
  margin: 0 4px;
  white-space: nowrap;
  border-radius: 50%;
  background-color: #D4D4D4;
  cursor: pointer;
  &:hover {
    background-color: darken(#D4D4D4, 15%);
  }
  &.active {
    background-color: #555555 !important;
  }
}
</style>
